/**
 * Shared types and enums for UI extension elements (buttons, cards, navigation).
 */

export enum GuiElementTypes {
    buttons = "buttons",
    cards = "cards",
}

export enum GuiElementView {
    article = "article",
    article_selection = "article-selection",
    parts = "parts",
    part_details = "part-details",
    activities = "activities",
    asset = "asset",
    mechanic = "mechanic",
    documentation = "documentation",
    product = "product",
    navigation_bar = "navigation-bar",
    dashboard = "dashboard",
}

export interface ViewContext {
    view: GuiElementView;
    topicId?: string;
    assetId?: string;
    productId?: string;
    orderable?: boolean;
    articleId?: string;
    mechanicId?: string;
    assemblyId?: string;
    partsListId?: string;
    mechanicPath?: string;
    contentMapId?: string;
}

interface ContextGuiBaseElement {
    id: string;
    url: string;
    openInCurrentTab: boolean;
    symbol: string;
    disabled: boolean;
}

/** Resolved gui button element ready for rendering */
export interface ContextGuiButtonElement extends ContextGuiBaseElement {
    label: string;
    tooltip: string;
}

/** Resolved gui card element ready for rendering */
export interface ContextGuiCardElement extends ContextGuiBaseElement {
    title: string;
    text: string;
    buttonLabel: string;
}

/** Raw GUI Button element config as returned by the API before expression evaluation */
export interface GuiButtonElement {
    id: string;
    symbol: string;
    showExpression: string;
    activeExpression?: string | null;
    urlExpression: string;
    openInCurrentTab: boolean;
    label: Record<string, string>;
    tooltip: Record<string, string>;
}

/** Raw GUI Card element config as returned by the API before expression evaluation */
export interface GuiCardElement {
    id: string;
    symbol: string;
    showExpression: string;
    activeExpression?: string | null;
    urlExpression: string;
    openInCurrentTab: boolean;
    title: Record<string, string>;
    text: Record<string, string>;
    buttonLabel: Record<string, string>;
}

/** Union of all raw GUI element configs returned by the API */
export type GuiElement = GuiButtonElement | GuiCardElement;

/** Union of all resolved GUI elements ready for rendering */
export type ContextGuiElement = ContextGuiButtonElement | ContextGuiCardElement;

/**
 * Maps each GuiElementTypes enum value to its resolved context element type.
 * Enables compile-time inference: the `type` parameter determines the return type
 * automatically — no manual generic annotation needed at call sites.
 */
export interface GuiElementTypeMap {
    [GuiElementTypes.buttons]: ContextGuiButtonElement;
    [GuiElementTypes.cards]: ContextGuiCardElement;
}

/**
 * Mapper function signature for transforming a raw GUI element into a resolved element.
 * Returns `null` if the element should be hidden (showExpression evaluates to false).
 */
export type ElementMapper<TOutput extends ContextGuiElement> = (
    uiElement: GuiElement,
    context: ExpressionContext,
    language: string
) => Promise<TOutput | null>;

/** Context enriched with user data for expression evaluation */
export type ExpressionContext = ViewContext & {
    roles: string[];
    rawClaims: object;
    email: string;
    language: string;
};

/** Result returned by the loader after fetching and merging context */
export interface GuiElementsResult {
    uiElements: GuiElement[];
    context: ExpressionContext;
}
